import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Input, SearchSelect } from "@zorni/components-lib";

import AutocompleteInput from "../../../common/autocompleteInput/AutocompleteInput";
import { getCountryOptions } from "../../../common/countries/countries";
import { ADDRESS_FIELDS, useAddressAutocomplete } from "./useAddressAutocomplete";

import styles from "./AddressForm.module.css";

/**
 * Saisie d'adresse complète (numéro, rue, complément, étage, pays, code
 * postal, ville) avec autocomplétion Photon sur code postal / ville, limitée
 * au pays choisi. Entièrement contrôlé : `value` = { address, countryCode },
 * `onChange(nextValue)` reçoit le prochain { address, countryCode } — y
 * compris quand un changement de pays réinitialise ville et code postal.
 *
 * `address` : { number, route, complement, stage, zipPostal, city }.
 * `errors` : objet de messages, indexé par "address.<champ>" (ex.
 * "address.city"), déjà le format utilisé par les validations existantes.
 */
const AddressForm = ({ value, onChange, errors }) => {
  const { t, i18n } = useTranslation();
  const { address, countryCode } = value;

  // Dernier pays validé : sert à savoir si un nouveau choix change réellement
  // de pays (donc invalide ville et code postal saisis pour l'ancien). Ignore
  // les passages à "" (saisie libre dans le champ pays, verrouillage en
  // cours) : seul un code non vide compte comme "validé", que ce soit via une
  // sélection ici ou une hydratation externe (chargement d'une adresse
  // existante par le parent).
  const committedCountryRef = useRef(countryCode || "");
  useEffect(() => {
    if (countryCode) committedCountryRef.current = countryCode;
  }, [countryCode]);

  const {
    activeField: activeAddressField,
    isLoading: isAddressLoading,
    hasRequested: hasAddressRequested,
    unavailable: isAddressUnavailable,
    place: selectedPlace,
    options: addressOptions,
    search: searchAddress,
    resetPlace,
    resolveSelection: resolveAddressSelection,
  } = useAddressAutocomplete({ countryCode });

  const countryOptions = useMemo(() => getCountryOptions(i18n.language), [i18n.language]);

  const setAddressField = (field, text) => {
    onChange({ address: { ...address, [field]: text }, countryCode });
  };

  // Sélection d'une suggestion Photon depuis "Code postal" ou "Ville". Le pays
  // n'est pas touché : les suggestions sont déjà limitées au pays choisi. Une
  // ville sans code connu vide le code postal (l'ancien appartenait à une
  // autre ville) ; un code postal sans ville laisse la ville en place.
  const applyAddressPatch = (patch) => {
    if (!patch) return;
    onChange({
      address: { ...address, zipPostal: patch.zipPostal, city: patch.city || address.city },
      countryCode,
    });
  };

  // Le pays n'est valide que choisi dans la liste : dès qu'on retape dedans il
  // est vidé (ville et code postal se verrouillent) ; le choisir à nouveau le
  // restaure. Un pays différent invalide ville et code postal, saisis pour
  // l'ancien.
  const handleCountryInput = () => onChange({ address, countryCode: "" });
  const handleCountryChange = (code) => {
    const next = code || "";
    if (next && committedCountryRef.current && committedCountryRef.current !== next) {
      resetPlace();
      onChange({ address: { ...address, zipPostal: "", city: "" }, countryCode: next });
      return;
    }
    onChange({ address, countryCode: next });
  };

  // Textes d'aide des champs dépendants du pays : d'abord le pays, ensuite la
  // disponibilité des suggestions, et pour le code postal l'invitation à
  // saisir les premiers chiffres quand la ville choisie n'en porte aucun.
  const cityHelperText = !countryCode
    ? t("commons.adress.selectCountryFirst", "Sélectionnez d'abord un pays")
    : isAddressUnavailable
      ? t(
          "commons.adress.suggestionsUnavailable",
          "Suggestions momentanément indisponibles : saisissez l'adresse manuellement"
        )
      : undefined;
  const zipHelperText = !countryCode
    ? t("commons.adress.selectCountryFirst", "Sélectionnez d'abord un pays")
    : selectedPlace && !address.zipPostal
      ? t(
          "commons.adress.zipHint",
          "Saisissez les premiers chiffres pour voir les codes postaux de cette ville"
        )
      : undefined;

  return (
    <div className={styles.pgrid}>
      <Input
        floatingLabel
        inputSize="medium"
        label={t("commons.adress.number")}
        name="number"
        value={address.number}
        onChange={(e) => setAddressField("number", e.target.value)}
        errors={errors?.["address.number"]}
      />
      <Input
        floatingLabel
        inputSize="medium"
        label={t("commons.adress.road")}
        name="route"
        value={address.route}
        onChange={(e) => setAddressField("route", e.target.value)}
        errors={errors?.["address.route"]}
      />
      <Input
        floatingLabel
        inputSize="medium"
        label={t("commons.adress.complement", "Complément")}
        name="complement"
        value={address.complement}
        onChange={(e) => setAddressField("complement", e.target.value)}
      />
      <Input
        floatingLabel
        inputSize="medium"
        label={t("commons.adress.stage", "Étage")}
        name="stage"
        value={address.stage}
        onChange={(e) => setAddressField("stage", e.target.value)}
      />
      <div className={styles.span2}>
        <SearchSelect t={t}
          floatingLabel
          inputSize="medium"
          label={t("commons.adress.country")}
          name="country"
          value={countryCode}
          options={countryOptions}
          onInput={handleCountryInput}
          onChange={handleCountryChange}
          errors={errors?.["address.country"]}
        />
      </div>
      <AutocompleteInput
        label={t("commons.adress.zipPostal")}
        name="zipPostal"
        value={address.zipPostal}
        options={activeAddressField === ADDRESS_FIELDS.POSTCODE ? addressOptions : []}
        isLoading={isAddressLoading && activeAddressField === ADDRESS_FIELDS.POSTCODE}
        showEmpty={hasAddressRequested && activeAddressField === ADDRESS_FIELDS.POSTCODE}
        emptyText={t("commons.adress.noSuggestion", "Aucune suggestion")}
        loadingText={t("commons.adress.loadingSuggestions", "Chargement…")}
        disabled={!countryCode}
        helperText={zipHelperText}
        onChange={(text) => {
          setAddressField("zipPostal", text);
          searchAddress(text, ADDRESS_FIELDS.POSTCODE);
        }}
        onSelect={(option) => applyAddressPatch(resolveAddressSelection(option.key))}
        errors={errors?.["address.zipPostal"]}
      />
      <AutocompleteInput
        label={t("commons.adress.city")}
        name="city"
        value={address.city}
        options={activeAddressField === ADDRESS_FIELDS.CITY ? addressOptions : []}
        isLoading={isAddressLoading && activeAddressField === ADDRESS_FIELDS.CITY}
        showEmpty={hasAddressRequested && activeAddressField === ADDRESS_FIELDS.CITY}
        emptyText={t("commons.adress.noSuggestion", "Aucune suggestion")}
        loadingText={t("commons.adress.loadingSuggestions", "Chargement…")}
        disabled={!countryCode}
        helperText={cityHelperText}
        onChange={(text) => {
          setAddressField("city", text);
          searchAddress(text, ADDRESS_FIELDS.CITY);
        }}
        onSelect={(option) => applyAddressPatch(resolveAddressSelection(option.key))}
        errors={errors?.["address.city"]}
      />
    </div>
  );
};

export default AddressForm;
